import type { ResearchStageKey } from '../../hooks/queries/use-research';

/**
 * FRONT-1601 — pure decision logic for the deep-research panel's "Finish
 * now" control (same reasoning as `research-panel-open.ts`: unit-testable
 * without React, one definition the banner and its test both consult so the
 * two can't drift).
 *
 * "Finish now" asks the running engine to stop searching and write up
 * whatever it already has, rather than cutting the run off outright (that's
 * still Stop, untouched by this ticket). It is only ever worth OFFERING
 * under conditions the engine can actually honour:
 *
 * - **Only the deep pipeline has a searching window to cut short.** The
 *   standard pipeline (`engine.ts`) runs one pass and writes — there is no
 *   "search a bit less" to ask for, so offering the control there would be a
 *   control that cannot be honoured. `deep` is never guessed here: it comes
 *   from the run's own phase evidence (`isDeepPipelinePhase`, mirrored onto
 *   `ResearchRun.deep`/`stage`), the same evidence the stage strip already
 *   trusts.
 * - **Finishing with nothing gathered can't produce an honest briefing.**
 *   The BACKEND is the authority here — it falls back to plain Stop
 *   behaviour (settles `cancelled`) when nothing USABLE has been gathered
 *   yet (`deep.ts`'s `usable.length === 0 && finishRequested` branch, a
 *   non-empty search RESPONSE, never a count of citation rows). This gate
 *   only APPROXIMATES that with the panel's own `sourceCount`
 *   (`useResearchCitations().length`, reused verbatim — never a second count
 *   of it) because that is the only signal available client-side without a
 *   dedicated endpoint. The two normally agree, but can diverge: a citation
 *   row can be filed for a search whose findings then fail to persist (e.g.
 *   the prior-findings read breaking on a Restate replay — see `deep.ts`
 *   FIX 2), or a search can return something usable that never manages to
 *   file a citation. Either way, the backend's own check is what actually
 *   protects against a fabricated briefing — this gate must NEVER be
 *   "fixed" to match the backend's count exactly, because approximating
 *   toward SHOWING the control more often than the backend would honour it
 *   is the safe direction (worst case: a click that behaves like Stop) and
 *   the backend must never be relaxed to match this count instead (that
 *   would be the fabricated-briefing bug FIX 2 protects against).
 *
 * Once available, a run that has already been asked to finish
 * (`finishRequestedAt` set) keeps the control on screen but INERT — the user
 * asked once, and a control that visibly did nothing on the second click
 * would read as broken. `"requested"` is deliberately not `"hidden"`: the
 * user needs to see their request registered, not have the button vanish
 * out from under them.
 *
 * FRONT-1601 (FIX 3): a fourth input, `stage`, and the rule ORDER below are
 * both load-bearing:
 *
 * 1. The baseline gate (no run id / not deep / no sources) always hides
 *    first — none of the later rules matter if the control was never
 *    offerable to begin with.
 * 2. `finishRequestedAt` set → `"requested"` MUST be checked BEFORE the
 *    `stage === 'writing'` rule below it. Requesting a finish is exactly
 *    what flips the engine into its write-up stage — so by the time a poll
 *    picks that stage up, checking rule 3 first would hide the very control
 *    whose click caused the transition, taking away the user's only
 *    feedback that their click registered.
 * 3. `stage === 'writing'` → `"hidden"`, checked only once rule 2 has ruled
 *    out "this run's own request caused it." Once write-up has genuinely
 *    begun there is nothing left to finish — this also covers the case
 *    where the CLOCK, not a click, ended the searching window
 *    (`budgetExhaustedPhase()`, "Time limit reached — writing the briefing
 *    from what was found…", is deep-only evidence, so `deep` alone would
 *    otherwise still show the control here). Pressing it in that state
 *    would set the latch after synthesis has already started reading
 *    findings, and the briefing would then falsely claim "You asked to
 *    finish this research early" while silently dropping the honest
 *    time-limit sentence.
 */
export type FinishNowState = 'hidden' | 'available' | 'requested';

export function finishNowState(input: {
  /** The running run's numeric id — absent during the brief optimistic
   *  window before the run row exists, same convention as the Stop button. */
  runId: number | null | undefined;
  /** Whether this run is known to be on the deep pipeline. `true` required
   *  exactly — `false` and `undefined` both mean "not deep enough to
   *  offer," and must never be conflated with "confirmed standard." */
  deep: boolean | undefined;
  /** The panel's own source count (`useResearchCitations().length`) — reuse
   *  the caller's existing tally, never recompute it. */
  sourceCount: number;
  /** Durable server flag — set once `finish` has been requested, null/absent
   *  otherwise. A page reload must still see a prior request as "requested,"
   *  which is exactly why this rides the run row rather than local state. */
  finishRequestedAt: string | null | undefined;
  /** FRONT-1601 (FIX 3): the run's current stage, same key the stage strip
   *  reads. `undefined` (no stage yet, or an unrecognised label) never hides
   *  by itself — only an explicit `'writing'` does. */
  stage: ResearchStageKey | undefined;
}): FinishNowState {
  const { runId, deep, sourceCount, finishRequestedAt, stage } = input;

  if (runId == null || deep !== true || sourceCount < 1) return 'hidden';
  if (finishRequestedAt) return 'requested';
  if (stage === 'writing') return 'hidden';
  return 'available';
}
